import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchAllPages } from '../../../../../../api/fetchAllPages';
import {
  getPoleLimits,
  updatePoleLimits,
} from '../../../../../../api/theHive/noticeCoverage';
import { PoleLimitData } from '../../../../../../api/theHive/noticeCoverage/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { PoleLimitsTable } from './components/PoleLimitsTable';
import { PoleSummaryCard } from './components/PoleSummaryCard';
import { Actions, Content, Header, SaveButton, SummaryGrid } from './styles';
import { PoleLimit } from './types';
import { buildDraftLimits, mapPole } from './utils';

// A notice can have hundreds of poles (the Proto Network edital has ~500), so a
// single page would silently truncate both the table and the totals card below —
// and this screen is where limits get edited, so a partial total is misleading.
const POLE_PAGE_SIZE = 200;
// Safety valve: caps the fan-out if the backend ever reports a huge page count.
const MAX_POLE_PAGES = 20;

export function NoticeDetailsPoleLimits(): JSX.Element {
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const [poles, setPoles] = useState<PoleLimit[]>([]);
  const [draftLimits, setDraftLimits] = useState<Record<number, string>>({});
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Ignores a superseded response (fast notice switch / reload after save).
  const runRef = useRef(0);

  const fetchPoles = useCallback(async () => {
    const runId = runRef.current + 1;
    runRef.current = runId;

    setIsLoading(true);
    try {
      const result = await fetchAllPages<PoleLimitData>(
        (page) => getPoleLimits(noticeId, page, POLE_PAGE_SIZE),
        { maxPages: MAX_POLE_PAGES }
      );
      if (runRef.current !== runId) return;
      const mapped = result.rows.map(mapPole);
      setPoles(mapped);
      setDraftLimits(buildDraftLimits(mapped));
      setIsTruncated(result.truncated);
    } catch (error) {
      if (runRef.current !== runId) return;
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (runRef.current === runId) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  useEffect(() => {
    if (!Number.isNaN(noticeId)) fetchPoles();
  }, [noticeId, fetchPoles]);

  const totals = useMemo(
    () =>
      poles.reduce(
        (summary, pole) => ({
          active: summary.active + pole.activeAccreditations,
          limit: summary.limit + pole.limit,
          waitlist: summary.waitlist + pole.waitlistCount,
        }),
        { active: 0, limit: 0, waitlist: 0 }
      ),
    [poles]
  );

  const invalidPoleIds = useMemo(
    () =>
      poles
        .filter((pole) => {
          const value = Number(draftLimits[pole.id]);
          return (
            draftLimits[pole.id] === '' ||
            !Number.isInteger(value) ||
            value < pole.activeAccreditations
          );
        })
        .map((pole) => pole.id),
    [draftLimits, poles]
  );

  const changedPoles = useMemo(
    () => poles.filter((pole) => Number(draftLimits[pole.id]) !== pole.limit),
    [poles, draftLimits]
  );

  const hasChanges = changedPoles.length > 0;
  const hasErrors = invalidPoleIds.length > 0;
  const availableAccreditations = totals.limit - totals.active;

  const summaryCards = [
    {
      label: 'Credenciados ativos',
      value: totals.active,
      hint: `Somados em ${poles.length} polos`,
    },
    {
      label: 'Limite total configurado',
      value: totals.limit,
      hint: 'Soma dos limites por polo',
    },
    {
      label: 'Vagas disponíveis',
      value: availableAccreditations,
      hint: 'Antes de atingir os limites',
    },
    {
      label: 'Em fila de espera',
      value: totals.waitlist,
      hint: 'Aguardando vaga nos polos cheios',
    },
  ];

  const handleLimitChange = (poleId: number, value: string): void => {
    if (!/^\d*$/.test(value)) return;

    setDraftLimits((current) => ({
      ...current,
      [poleId]: value,
    }));
  };

  const handleReset = (): void => {
    setDraftLimits(buildDraftLimits(poles));
  };

  const handleSave = async (): Promise<void> => {
    if (hasErrors || !hasChanges) return;
    setIsSaving(true);
    try {
      const response = await updatePoleLimits(noticeId, {
        limits: changedPoles.map((pole) => ({
          pole_id: pole.id,
          max_companies: Number(draftLimits[pole.id]),
        })),
      });
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Limites atualizados com sucesso.', false);
      await fetchPoles();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Content>
      <Header>
        <Box>
          <Typography color="text.primary" fontSize={22} fontWeight={600}>
            Limites de credenciamento por polo
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            Defina quantos credenciados ativos cada polo pode ter no edital
            {notice ? ` #${notice}` : ''}.
          </Typography>
        </Box>
      </Header>

      <Alert severity="info">
        Ao atingir o limite, novos credenciamentos para o polo entram em fila de
        espera. Descredenciamentos liberam vagas novamente.
      </Alert>

      {isTruncated && (
        <Alert severity="warning">
          Este edital tem mais polos do que o limite de carga da tela: a tabela
          e os totais abaixo cobrem apenas os {poles.length} primeiros polos,
          não o edital inteiro. Salvar continua seguro (só os polos alterados
          são enviados), mas não use os totais como base de decisão.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <SummaryGrid>
            {summaryCards.map((card) => (
              <PoleSummaryCard
                key={card.label}
                label={card.label}
                value={card.value}
                hint={card.hint}
              />
            ))}
          </SummaryGrid>

          <PoleLimitsTable
            poles={poles}
            draftLimits={draftLimits}
            invalidPoleIds={invalidPoleIds}
            onLimitChange={handleLimitChange}
          />

          {hasErrors && (
            <Alert severity="error">
              O limite de um polo nunca pode ser menor que a quantidade de
              credenciados ativos.
            </Alert>
          )}

          <Actions>
            <Button
              disabled={!hasChanges || isSaving}
              onClick={handleReset}
              sx={{ textTransform: 'none' }}
              variant="text"
            >
              Descartar alterações
            </Button>
            <SaveButton
              disabled={!hasChanges || hasErrors || isSaving}
              onClick={handleSave}
              variant="contained"
            >
              {isSaving ? 'Salvando...' : 'Salvar limites'}
            </SaveButton>
          </Actions>
        </>
      )}
    </Content>
  );
}
